/**
 * Parsing at the API boundary: a value that breaks its schema is reported and refused, so
 * nothing past this point guards what the schema promises. See docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import type {
    AnswerOrigin, ContractViolation, DroppedRow, KeyedFrame, KeyedOption, PathSegment,
    RowKeyReader, ViolationOrigin, ViolationSink, ViolationSource,
} from '@/types/contract';
import { ViolationKind } from '@/types/contract';

import { ApiError } from './apiError';
import { contractReporter } from './contractReporter';
import { FieldsSchema, rowDrops } from './listOf';
import { getFilled } from './mediaServerFill';
import { mediaServerStrictness } from './mediaServerStrictness';

// Resolves a bare path; the host never reaches the result.
const URL_BASE = 'http://boundary.invalid';

const RowsSchema = z.array(z.unknown());

/** Text that must be JSON. A proxy's error page answers 200 too, and breaks the contract whole. */
export const JsonTextSchema = z.string().transform((text, ctx): unknown => {
    try {
        const parsedValue: unknown = JSON.parse(text);
        return parsedValue;
    } catch {
        ctx.issues.push({ code: 'custom', message: 'Not JSON', input: text });
        return z.NEVER;
    }
});

/** An answer that broke its schema. Callers surface it as they surface any `ApiError`. */
export class ContractError extends ApiError {
    readonly issues: z.core.$ZodIssue[];

    constructor(origin: AnswerOrigin, issues: z.core.$ZodIssue[]) {
        super(`Unexpected answer from ${origin.endpoint}`, origin.status);
        this.name = 'ContractError';
        this.issues = issues;
    }
}

function getMismatch(
    origin: ViolationOrigin, issues: z.core.$ZodIssue[], path?: PathSegment[],
): ContractViolation {
    return {
        kind: ViolationKind.Mismatch,
        endpoint: origin.endpoint,
        source: origin.source,
        issues,
        ...(path === undefined ? {} : { path }),
        atMs: Date.now(),
    };
}

function getUnknownFrame(
    origin: ViolationOrigin, issues: z.core.$ZodIssue[], frameType: string,
): ContractViolation {
    return {
        kind: ViolationKind.UnknownFrame,
        endpoint: origin.endpoint,
        source: origin.source,
        issues,
        frameType,
        atMs: Date.now(),
    };
}

function reportDropped(
    sink: ViolationSink, origin: ViolationOrigin, droppedRows: DroppedRow[], prefix: PathSegment[],
): void {
    for (const { path, issues } of droppedRows) {
        sink.report(getMismatch(origin, issues, [...prefix, ...path]));
    }
}

/** How a parse reports: where its value sits in the answer, and what the whole failing says. */
interface ParseReport {
    prefix?: PathSegment[];
    reportFailure: (issues: z.core.$ZodIssue[]) => void;
}

/**
 * Parses, reporting the rows dropped once the value as a whole has parsed, or its failure. A
 * media_server's value the deployment lets the boundary repair is parsed again with what an
 * older server left out filled in, and that result is used; what broke is reported all the same.
 */
function getParsed<S extends z.ZodType>(
    schema: S,
    rawValue: unknown,
    origin: ViolationOrigin,
    sink: ViolationSink,
    { prefix = [], reportFailure }: ParseReport,
): z.ZodSafeParseResult<z.output<S>> {
    const strictCollected = rowDrops.collect(() => schema.safeParse(rawValue));
    const isWhole = strictCollected.result.success && strictCollected.droppedRows.length === 0;
    if (strictCollected.result.success) {
        reportDropped(sink, origin, strictCollected.droppedRows, prefix);
    } else {
        reportFailure(strictCollected.result.error.issues);
    }
    if (isWhole || !mediaServerStrictness.isLenient(origin.source)) {
        return strictCollected.result;
    }
    const lenient = rowDrops.collect(() => schema.safeParse(getFilled(schema, rawValue)));
    if (!lenient.result.success) {
        return strictCollected.result;
    }
    // The rows the strict parse dropped are what broke too, though its whole failed; a strict
    // parse that fails leaves them unreported, since nothing of its value is used.
    if (!strictCollected.result.success) {
        reportDropped(sink, origin, strictCollected.droppedRows, prefix);
    }
    return lenient.result;
}

/**
 * Whether an option of a union discriminated by `discriminator`, or one nested in it,
 * declares `tag`.
 */
function isDeclaredBy(option: unknown, discriminator: string, tag: string): boolean {
    if (option instanceof z.ZodDiscriminatedUnion) {
        const nestedItems: readonly unknown[] = option.options;
        return nestedItems.some((inner) => isDeclaredBy(inner, discriminator, tag));
    }
    if (!(option instanceof z.ZodObject)) {
        return false;
    }
    const shape: Record<string, unknown> = option.shape;
    const tagSchema = shape[discriminator];
    return tagSchema instanceof z.ZodType && tagSchema.safeParse(tag).success;
}

/** The discriminated union a frame schema dispatches on: the schema, or an option of a union. */
function getFrameUnion(schema: z.ZodType): z.ZodDiscriminatedUnion | null {
    if (schema instanceof z.ZodDiscriminatedUnion) {
        return schema;
    }
    if (!(schema instanceof z.ZodUnion)) {
        return null;
    }
    const options: readonly unknown[] = schema.options;
    const union = options.find((option) => option instanceof z.ZodDiscriminatedUnion);
    return union instanceof z.ZodDiscriminatedUnion ? union : null;
}

/**
 * A frame the union declares but that breaks it is reported with the discriminated union's
 * issues, which name the field; a `z.union` around it only says it matched no option.
 */
function getFrameIssues(
    schema: z.ZodType, frame: unknown, issues: z.core.$ZodIssue[],
): z.core.$ZodIssue[] {
    const union = getFrameUnion(schema);
    if (union === null || union === schema) {
        return issues;
    }
    return union.safeParse(frame).error?.issues ?? issues;
}

/**
 * The type a frame names that its union declares no option for. Only a union discriminated by a
 * field has unknown types, and only a string in that field is a type a newer backend may have
 * added; a frame with any other value there is a broken frame.
 */
function getUndeclaredType(schema: z.ZodType, frame: unknown): string | null {
    const union = getFrameUnion(schema);
    if (union === null) {
        return null;
    }
    const { discriminator } = union.def;
    const fields = FieldsSchema.safeParse(frame);
    const tag = fields.success ? fields.data[discriminator] : undefined;
    if (typeof tag !== 'string' || isDeclaredBy(union, discriminator, tag)) {
        return null;
    }
    return tag;
}

/** A URL's path without its query; a relative URL is read against a base it never reaches. */
export function getEndpoint(url: string): string {
    return new URL(url, URL_BASE).pathname;
}

/** Names an answer by its URL's path and the source the transport knows it came from. */
export function getAnswerOrigin(
    url: string, status: number, source: ViolationSource,
): AnswerOrigin {
    return {
        endpoint: getEndpoint(url),
        source,
        status,
    };
}

export function parseOne<S extends z.ZodType>(
    schema: S, rawValue: unknown, origin: AnswerOrigin, sink: ViolationSink = contractReporter,
): z.output<S> {
    const result = getParsed(schema, rawValue, origin, sink, {
        reportFailure: (issues): void => {
            sink.report(getMismatch(origin, issues));
        },
    });
    if (result.success) {
        return result.data;
    }
    throw new ContractError(origin, result.error.issues);
}

/**
 * A list keeps the rows that parse, in order; only an answer that is no list at all fails.
 * `getKey` names a row in a report; a row it cannot name is named by its index.
 */
export function parseRows<S extends z.ZodType>(
    itemSchema: S,
    rawValue: unknown,
    origin: AnswerOrigin,
    sink: ViolationSink = contractReporter,
    getKey?: RowKeyReader,
): z.output<S>[] {
    const rows = parseOne(RowsSchema, rawValue, origin, sink);
    return rows.flatMap((row, index): z.output<S>[] => {
        const place = [getKey?.(row) ?? index];
        const result = getParsed(itemSchema, row, origin, sink, {
            prefix: place,
            reportFailure: (issues): void => {
                sink.report(getMismatch(origin, issues, place));
            },
        });
        return result.success ? [result.data] : [];
    });
}

/** As parseFrame, for a frame already read from its text, such as a value inside a frame. */
export function parseFrameValue<S extends z.ZodType>(
    schema: S, frame: unknown, origin: ViolationOrigin, sink: ViolationSink = contractReporter,
): z.output<S> | null {
    const result = getParsed(schema, frame, origin, sink, {
        reportFailure: (issues): void => {
            const frameType = getUndeclaredType(schema, frame);
            sink.report(frameType === null
                ? getMismatch(origin, getFrameIssues(schema, frame, issues))
                : getUnknownFrame(origin, issues, frameType));
        },
    });
    return result.success ? result.data : null;
}

/**
 * A stream or socket frame: the parsed frame, or null once it is reported and dropped. A frame
 * naming a type its discriminated union does not declare is reported as an unknown frame.
 */
export function parseFrame<S extends z.ZodType>(
    schema: S, text: string, origin: ViolationOrigin, sink: ViolationSink = contractReporter,
): z.output<S> | null {
    const json = JsonTextSchema.safeParse(text);
    if (!json.success) {
        sink.report(getMismatch(origin, json.error.issues));
        return null;
    }
    return parseFrameValue(schema, json.data, origin, sink);
}

/**
 * As parseFrame, for a stream that carries frames other senders built: each frame's violations
 * are graded by the sender `getSource` reads from it. A text that is no JSON names nobody.
 */
export function parseSourcedFrame<S extends z.ZodType>(
    schema: S,
    text: string,
    origin: ViolationOrigin,
    getSource: (frame: unknown) => ViolationSource,
    sink: ViolationSink = contractReporter,
): z.output<S> | null {
    const json = JsonTextSchema.safeParse(text);
    if (!json.success) {
        sink.report(getMismatch(origin, json.error.issues));
        return null;
    }
    const frameOrigin = {
        ...origin,
        source: getSource(json.data),
    };
    return parseFrameValue(schema, json.data, frameOrigin, sink);
}

/**
 * The schema of the one option whose key the frame has. A frame with none of the keys, or with
 * more than one, fits no option.
 */
function getKeyedSchema<O extends readonly KeyedOption[]>(
    options: O, frame: unknown,
): O[number]['schema'] | z.ZodNever {
    const fields = FieldsSchema.safeParse(frame);
    const matches = fields.success
        ? options.filter(({ key }) => Object.hasOwn(fields.data, key))
        : [];
    const [onlyKeyedOption] = matches;
    if (matches.length === 1 && onlyKeyedOption !== undefined) {
        return onlyKeyedOption.schema;
    }
    const keys = options.map(({ key }) => key).join(', ');
    return z.never({ error: `Expected exactly one of the keys ${keys}` });
}

/** A kind of keyed frame whose parse also names the kind, as `{ key, frame }`. */
export function getKeyedOption<const K extends string, S extends z.ZodType>(
    key: K, schema: S,
): KeyedOption<z.ZodType<KeyedFrame<K, z.output<S>>>> {
    return {
        key,
        schema: schema.transform((frame): KeyedFrame<K, z.output<S>> => ({
            key,
            frame,
        })),
    };
}

/**
 * A frame of a stream whose frames carry no type field: each kind has a key that only it
 * carries, and the frame is parsed against that kind's schema alone, so the report names the
 * broken field. Such a stream has no unknown frames.
 */
export function parseKeyedFrame<O extends readonly KeyedOption[]>(
    options: O, text: string, origin: ViolationOrigin, sink: ViolationSink = contractReporter,
): z.output<O[number]['schema']> | null {
    const json = JsonTextSchema.safeParse(text);
    if (!json.success) {
        sink.report(getMismatch(origin, json.error.issues));
        return null;
    }
    return parseFrameValue(getKeyedSchema(options, json.data), json.data, origin, sink);
}
